import * as _ from 'lodash';
import * as Pappa from 'papaparse'
import * as React from 'react'
import { Alert, Button, Card, CardBody, CardFooter, Col, Form, FormGroup, Input, InputGroup, Row } from 'reactstrap';
import { HttpRequest } from '../../../util/http';
import { userData } from '../../../util/userData';
import { HierarchySelect } from '../../hierarchies/HierarchySelect';
import { ContentCardHeader } from '../../ui/ContentCardHeader';
import { DataGrid } from '../../ui/DataGrid';

interface IAvidData {
  client: string;
  label: string;
  customer: any;
  avid: string;
  hierarchy: string;
}

interface IUploadAvidsProps {
  onSuccess?: () => Promise<any> | void
}

interface IUploadAvidsState {
  numOfDuplicatesWereRemoved: number
  avids: IAvidData[]
  success: boolean;
  uploading: boolean;
  file?: File
  uploadError?: string
  csvError?: string;
  hierarchy: string;
}

export class UploadAvids extends React.Component<IUploadAvidsProps, IUploadAvidsState> {
  state: IUploadAvidsState = {
    avids: [],
    numOfDuplicatesWereRemoved: 0,
    success: false,
    uploading: false,
    hierarchy: ''
  }

  formElem: HTMLFormElement

  reset() {
    const file = undefined //reset
    const avids = [] //reset
    const numOfDuplicatesWereRemoved = 0
    const uploadError = undefined
    const csvError = undefined
    const hierarchy = ''

    this.setState({ file, avids, numOfDuplicatesWereRemoved, uploadError, csvError, hierarchy })

    if (this.formElem) {
      this.formElem.reset()
    }
  }

  updateFile(e: React.ChangeEvent<HTMLInputElement>) {
    const fileReader = new FileReader()
    const inputElem = e.target
    const file = inputElem.files[0]
    const success = undefined
    let avids: IAvidData[] = []
    let csvError

    if (!file) { return }

    this.setState({ file, csvError, avids, success })

    fileReader.onload = () => {
      const csvData = String(fileReader.result);
      const customer = userData.getCurrentProject();
      let resAvidData: string[][] = [];

      try {
        resAvidData = Pappa.parse(csvData).data;
      } catch (err) {
        csvError = err.message;
        this.setState({ csvError });
        return;
      }

      const uniqueAvidData = _.uniqBy(resAvidData, (data) => `${data[0]}_${data[1]}`);
      const numOfDuplicatesWereRemoved = resAvidData.length - avids.length;
      avids = uniqueAvidData.filter((value) => {
        return value.length === 2
      }).map((singleAvid) => {
        const client = singleAvid[0];
        const label = singleAvid[1];
        const avid = client.replace(/\W_/g, 'g') + '_' + label.replace(/\W/g, '');
        const hierarchy = '';
        return { client, label, customer, avid, hierarchy };
      });

      this.setState({ avids, numOfDuplicatesWereRemoved });
    }

    fileReader.readAsText(file, 'utf-8')
  }

  removeAvid(avid: IAvidData) {

    const { avids } = this.state
    const index = avids.findIndex((storedAvid) => storedAvid.avid === avid.avid);

    if (index >= 0) {
      avids.splice(index, 1)

      this.setState({ avids })

      if (avids.length <= 0 && this.formElem) {
        const file = undefined

        this.formElem.reset()

        this.setState({ file })
      }
    }
  }

  fireOnSuccessPromise() {
    const { onSuccess } = this.props
    let onSuccessPromise = onSuccess ? onSuccess() : undefined

    if (!onSuccessPromise) {
      onSuccessPromise = Promise.resolve();
    }

    return onSuccessPromise;
  }

  updateHierarchy(hierarchy: string) {
    const { avids } = this.state

    avids.forEach((avid) => avid.hierarchy = hierarchy)

    this.setState({ hierarchy, avids })
  }

  uploadAvids(e: React.FormEvent<HTMLFormElement>) {
    const { avids } = this.state
    const method = 'POST'
    const body = JSON.stringify({ avids })

    e.preventDefault()

    this.setState({ uploadError: undefined, uploading: true })

    HttpRequest.fetch('/scripts/avids/avids_add_all', { method, body }).then((res: any) => {
      const uploadError = typeof res.error === 'string' ? res.error : undefined

      if (!uploadError) {
        this.fireOnSuccessPromise().then(() => {
          this.setState({ success: true })
          setTimeout(() => this.setState({ success: undefined }), 5 * 1000)

          this.reset()
        })

      } else {
        this.setState({ uploadError })
      }

      this.setState({ uploading: false })
    })
  }

  render() {
    const { file, avids, numOfDuplicatesWereRemoved, uploadError, success, uploading, csvError } = this.state
    const fileName = file ? file.name : 'Choose CSV File'
    const duplicatesMoreThanZero = numOfDuplicatesWereRemoved > 0
    const duplicatesMoreThanOne = numOfDuplicatesWereRemoved > 1

    return (
      <Card>
        <Form onSubmit={(e) => this.uploadAvids(e)}
          innerRef={(formElem) => this.formElem = formElem}>
          <ContentCardHeader header="Upload Avids" sticky borderBottom />
          <CardBody>
            {csvError &&
              <FormGroup>
                <Alert color="danger">
                  <i className="fas fa-fw fa-exclamation-triangle" />
                  <span>&nbsp;Unable to parse CSV file.</span>
                  <span>&nbsp;Verify the file is a proper CSV and upload again:</span>
                  <span>&nbsp;{csvError}</span>
                </Alert>
              </FormGroup>
            }
            <Row>
              <Col md={6}>
                <FormGroup>
                  <label>CSV File</label>
                  <InputGroup>
                    <div className="custom-file">
                      <Input type="file" className="custom-file-input"
                        style={{ cursor: 'pointer' }}
                        accept=".csv,text/csv"
                        disabled={uploading || !!uploadError}
                        onChange={(e) => this.updateFile(e)} />
                      <label className="custom-file-label">{fileName}</label>
                    </div>
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <label>Hierarchy Level</label>
                  <HierarchySelect required
                    readOnly={uploading || !!uploadError}
                    onChange={(hierarchy) => this.updateHierarchy(hierarchy)}
                    value={this.state.hierarchy} />
                </FormGroup>
              </Col>
            </Row>

            {duplicatesMoreThanZero &&
              <FormGroup>
                <Alert color="warning">
                  <i className="fas fa-fw fa-sticky-note" />
                  <span>&nbsp;Note:&nbsp;{numOfDuplicatesWereRemoved.toLocaleString()}&nbsp;{duplicatesMoreThanOne ? 'duplicates were' : 'duplicate was'}&nbsp;removed.</span>
                </Alert>
              </FormGroup>
            }

            {avids.length > 0 &&
              <FormGroup>
                <DataGrid<IAvidData>
                  records={avids}
                  columns={[
                    { header: 'Client Id', property: 'client' },
                    { header: 'Labels', property: 'label' },
                    {
                      header: 'Remove', width: 100, align: 'center', render: (avid) =>
                        <Button color="link"
                          href="javascript:void(0)"
                          onClick={() => this.removeAvid(avid)}
                          disabled={success || uploading || !!uploadError}>
                          <i className="fas fa-fw fa-trash-alt" />
                        </Button>
                    }
                  ]}
                />
              </FormGroup>
            }
          </CardBody>
          <CardFooter className="d-flex justify-content-end">
            {uploadError &&
              <Alert color="danger" className="alert-input-height mr-2">
                <i className="fas fa-fw fa-exclamation-triangle" />
                <span>&nbsp;{uploadError}</span>
              </Alert>
            }
            {success &&
              <Alert color="success" className="alert-input-height mr-2">
                <i className="fas fa-fw fa-check-square" />
                <span>&nbsp;Avids Successfully Uploaded!</span>
              </Alert>
            }
            {!success && !uploadError &&
              <Button type="submit" color="action" className="mr-2"
                disabled={avids.length <= 0 || uploading}>
                {uploading ?
                  <>
                    <span>Uploading&nbsp;</span>
                    <i className="fas fa-fw fa-spin fa-cog" />
                  </>
                  :
                  <span>Upload</span>
                }
              </Button>
            }
            {!success &&
              <Button color="white" disabled={uploading} onClick={() => this.reset()}>
                <span>Reset</span>
              </Button>
            }
          </CardFooter>
        </Form>
      </Card >
    )
  }
}
